import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';
import { Plus, Tag, Calendar as CalendarIcon, Percent, DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';

export function CouponManager({ coupons = [], setCoupons, currencySymbol = '₦' }) {
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [hasUsageLimit, setHasUsageLimit] = useState(false);

  const resetForm = () => {
    setCode('');
    setDiscountType('percentage');
    setDiscountValue('');
    setUsageLimit('');
    setExpiryDate(null);
    setHasExpiry(false);
    setHasUsageLimit(false);
    setShowForm(false);
  };

  const addCoupon = () => {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) return;
    if (!discountValue || parseFloat(discountValue) <= 0) return;
    if (discountType === 'percentage' && parseFloat(discountValue) > 100) return;
    if (coupons.some(c => c.code === trimmedCode)) return;

    const newCoupon = {
      code: trimmedCode,
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      usage_limit: hasUsageLimit && usageLimit ? parseInt(usageLimit) : null,
      expiry_date: hasExpiry && expiryDate ? expiryDate.toISOString() : null,
      is_active: true
    };

    setCoupons([...coupons, newCoupon]);
    resetForm();
  };

  const toggleActive = (idx) => {
    const updated = [...coupons];
    updated[idx] = { ...updated[idx], is_active: !updated[idx].is_active };
    setCoupons(updated);
  };

  return (
    <Card data-testid="coupon-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Coupons / Discounts
          </CardTitle>
          {!showForm && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
              data-testid="add-coupon-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Coupon
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Existing Coupons */}
        {coupons.map((coupon, idx) => (
          <div
            key={coupon.coupon_id || coupon.code}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg border",
              coupon.is_active ? "bg-muted/30" : "bg-muted/10 opacity-60"
            )}
            data-testid={`coupon-item-${coupon.code}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded bg-primary/10">
                <Tag className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-sm">{coupon.code}</span>
                  {!coupon.is_active && (
                    <Badge variant="outline" className="text-xs bg-muted text-muted-foreground" data-testid={`coupon-inactive-badge-${idx}`}>
                      Inactive
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {coupon.discount_type === 'percentage'
                    ? `${coupon.discount_value}% off`
                    : `${currencySymbol}${coupon.discount_value} off`}
                  {coupon.usage_limit ? ` · Limit: ${coupon.usage_limit} uses` : ''}
                  {coupon.expiry_date ? ` · Expires: ${format(new Date(coupon.expiry_date), 'MMM d, yyyy')}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={coupon.is_active ? 'outline' : 'default'}
                size="sm"
                onClick={() => toggleActive(idx)}
                data-testid={`coupon-toggle-active-${idx}`}
              >
                {coupon.is_active ? 'Deactivate' : 'Reactivate'}
              </Button>
            </div>
          </div>
        ))}

        {coupons.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No coupons yet. Add one to offer discounts to your attendees.
          </p>
        )}

        {/* Add Coupon Form */}
        {showForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/20" data-testid="coupon-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Code</Label>
                <Input
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                  placeholder="e.g. EARLYBIRD"
                  className="font-mono uppercase"
                  maxLength={20}
                  data-testid="coupon-code-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Discount Type</Label>
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger data-testid="coupon-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">
                      <span className="flex items-center gap-1"><Percent className="w-3 h-3" /> Percentage</span>
                    </SelectItem>
                    <SelectItem value="fixed">
                      <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> Fixed Amount</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">
                {discountType === 'percentage' ? 'Discount (%)' : `Discount Amount (${currencySymbol})`}
              </Label>
              <Input
                type="number"
                value={discountValue}
                onChange={e => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 500'}
                min="1"
                max={discountType === 'percentage' ? '100' : undefined}
                data-testid="coupon-value-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Set usage limit</Label>
              <Switch checked={hasUsageLimit} onCheckedChange={setHasUsageLimit} data-testid="coupon-limit-toggle" />
            </div>
            {hasUsageLimit && (
              <Input
                type="number"
                value={usageLimit}
                onChange={e => setUsageLimit(e.target.value)}
                placeholder="Max number of uses"
                min="1"
                data-testid="coupon-limit-input"
              />
            )}

            <div className="flex items-center justify-between">
              <Label className="text-xs">Set expiry date</Label>
              <Switch checked={hasExpiry} onCheckedChange={setHasExpiry} data-testid="coupon-expiry-toggle" />
            </div>
            {hasExpiry && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !expiryDate && "text-muted-foreground")} data-testid="coupon-expiry-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiryDate ? format(expiryDate, 'PPP') : 'Pick expiry date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={setExpiryDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                onClick={addCoupon}
                disabled={!code.trim() || !discountValue || parseFloat(discountValue) <= 0}
                data-testid="coupon-save-btn"
              >
                Add Coupon
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="coupon-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
